import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useReview } from '../contexts/ReviewContext';

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=600&fit=crop&q=80';

const ReviewList: React.FC = () => {
  const { reviewList, loading } = useReview();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const publishedReviews = useMemo(() =>
    reviewList.filter(r => r.is_published),
    [reviewList]
  );

  const years = useMemo(() => {
    const yearSet = new Set(publishedReviews.map(r => new Date(r.created_at).getFullYear()));
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [publishedReviews]);

  const filteredReviews = useMemo(() =>
    selectedYear
      ? publishedReviews.filter(r => new Date(r.created_at).getFullYear() === selectedYear)
      : publishedReviews,
    [publishedReviews, selectedYear]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex items-center justify-center min-h-[400px] mt-20">
          <div className="text-sm sm:text-base text-gray-500">로딩 중...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header */}
      <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920&h=600&fit=crop&q=80"
            alt="수강후기"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">참여마당</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">수강후기</h1>
          <div className="w-16 sm:w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 py-8 sm:py-10 md:py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 연도 필터 + 카운트 */}
          <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm sm:text-base text-gray-600">
              총 <span className="font-bold text-green-600">{filteredReviews.length}</span>개의 후기
            </p>
            {years.length > 1 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedYear(null)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    selectedYear === null
                      ? 'bg-[#48c9a6] text-white'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  전체
                </button>
                {years.map(year => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                      selectedYear === year
                        ? 'bg-[#48c9a6] text-white'
                        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filteredReviews.length === 0 ? (
            <div className="text-center py-12 sm:py-16 md:py-20">
              <p className="text-gray-500 text-base sm:text-lg">등록된 수강후기가 없습니다.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
              {filteredReviews.map((review) => (
                <div
                  key={review.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/community/reviews/${review.id}`)}
                >
                  {/* 썸네일 */}
                  <div className="relative h-48 sm:h-52 md:h-56 bg-gray-200 overflow-hidden">
                    <img
                      src={review.thumbnail_url || PLACEHOLDER_IMAGE}
                      alt={review.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                  </div>

                  {/* 내용 */}
                  <div className="p-3 sm:p-4">
                    <h3 className="text-sm sm:text-base font-bold text-gray-800 mb-1 sm:mb-2 line-clamp-2">
                      {review.title}
                    </h3>
                    <div className="text-xs sm:text-sm text-gray-500">
                      <span className="text-[10px] sm:text-xs">
                        {new Date(review.created_at).toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ReviewList;
